import { Controller } from "@hotwired/stimulus"

// Sube una imagen al endpoint /image/create (S3UploaderController) y escribe
// la URL `original` resultante en un hidden input dentro del form padre.
// Disenado para campos de tipo "URL de imagen" como `css.backgroundImage`
// del editor visual de paginas.
//
// Replica el UX de UploadCard.tsx + UploadToCdn del frontend React: card
// 140x140 con preview, boton subir/reemplazar y boton eliminar.
//
// Tras escribir la URL, dispara un evento `change` con `bubbles: true` sobre
// el hidden input para que el `manage--form-auto-submit` del form padre lo
// detecte y persista el cambio sin recargar.
export default class extends Controller {
  static targets = [
    "urlInput",
    "preview",
    "placeholder",
    "fileInput",
    "uploadBtn",
    "uploadLabel",
    "deleteBtn",
    "loading",
    "error"
  ]

  static values = {
    url:           { type: String, default: "/image/create" },
    uploadLabel:   { type: String, default: "Subir imagen" },
    replaceLabel:  { type: String, default: "Reemplazar" },
    invalidFormat: { type: String, default: "El archivo no es un formato de imagen" },
    uploadError:   { type: String, default: "Error al subir la imagen" }
  }

  openFilePicker(event) {
    event.preventDefault()
    if (this.fileInputTarget.disabled) return
    this.fileInputTarget.click()
  }

  async fileSelected(event) {
    const file = event.target.files[0]
    if (!file) return

    if (!file.type.startsWith("image/")) {
      this.fileInputTarget.value = ""
      this.#showError(this.invalidFormatValue)
      return
    }

    this.#hideError()
    this.#setLoading(true)

    try {
      const formData = new FormData()
      formData.append("file", file)

      const csrf = document.querySelector("meta[name='csrf-token']")?.content
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: csrf ? { "X-CSRF-Token": csrf } : {},
        body: formData
      })
      const data = await response.json()

      if (data?.success && data?.original) {
        this.#setUrl(data.original)
      } else {
        this.#showError(this.uploadErrorValue)
      }
    } catch (e) {
      console.error("[image-url-upload] upload failed:", e)
      this.#showError(this.uploadErrorValue)
    } finally {
      this.#setLoading(false)
      this.fileInputTarget.value = ""
    }
  }

  remove(event) {
    event.preventDefault()
    this.#setUrl("")
  }

  #setUrl(url) {
    this.urlInputTarget.value = url
    this.urlInputTarget.dispatchEvent(new Event("change", { bubbles: true }))

    if (url) {
      this.previewTarget.src = url
      this.previewTarget.classList.remove("hidden")
      if (this.hasPlaceholderTarget) this.placeholderTarget.classList.add("hidden")
      this.deleteBtnTarget.classList.remove("hidden")
      this.uploadLabelTarget.textContent = this.replaceLabelValue
    } else {
      this.previewTarget.removeAttribute("src")
      this.previewTarget.classList.add("hidden")
      if (this.hasPlaceholderTarget) this.placeholderTarget.classList.remove("hidden")
      this.deleteBtnTarget.classList.add("hidden")
      this.uploadLabelTarget.textContent = this.uploadLabelValue
    }
  }

  #setLoading(loading) {
    if (this.hasLoadingTarget) this.loadingTarget.classList.toggle("hidden", !loading)
    this.uploadBtnTarget.disabled = loading
    if (this.hasDeleteBtnTarget) this.deleteBtnTarget.disabled = loading
    this.fileInputTarget.disabled = loading
  }

  #showError(msg) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = msg
    this.errorTarget.classList.remove("hidden")
  }

  #hideError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.classList.add("hidden")
  }
}
